import React, { useState, useEffect } from 'react';
import { 
  Box,
  Heading,
  VStack,
  useToast,
  Card,
  CardHeader,
  CardBody,
  Divider,
  useColorModeValue,
  Text,
} from '@chakra-ui/react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from '../../axiosConfig';
import { useTranslation } from 'react-i18next';
import { useUser } from '../../contexts/UserContext';
import OrganizationGeneralFields from './OrganizationGeneralFields';
import OrganizationBillingFields from './OrganizationBillingFields';
import TourModeActionButton from './TourModeActionButton';

const OrganizationForm = ({ id: propId }) => {
  const { t } = useTranslation();
  const { id: paramId } = useParams();
  const location = useLocation();
  const id = propId || paramId;
  const [organization, setOrganization] = useState({
    name: '',
    contact: '',
    title: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    country: '',
    zip: '',
    notes: '',
    // Billing Fields
    billing_same_as_org: true,
    billing_company_name: '',
    billing_address: '',
    billing_zip: '',
    billing_city: '',
    billing_state: '',
    billing_country: '',
    vat_or_tax_number: '',
    currency: '',
    billing_method: '',
    billing_email: '',
    billing_method_other: '',
    reference_person: '',
    reference_number: '',
    cost_center: '',
    billing_notes: '',
    peppol: '',
    gln: '',
    organisationsnummer: ''
  });
  const navigate = useNavigate();
  const toast = useToast();
  const isHub = location.pathname.startsWith("/hub");
  const isOrganizationSelfSettings =
    isHub || location.pathname.startsWith("/app/organization/organization-settings");
  const { user } = useUser();
  const isTour = organization?.tour_mode === true;

  useEffect(() => {
    if (id) {
      const fetchOrganization = async () => {
        try {
          const response = await axios.get(`/organizations/${id}`);
          setOrganization(response.data);
        } catch (error) {
          console.error('Error fetching organization:', error);
        }
      };
      fetchOrganization();
    }
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setOrganization((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (user?.user_type === 'tour') {
      toast({
        title: t("common.changes_not_saved_title"),
        description: t("common.changes_not_saved_description"),
        status: "info",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      if (id) {
        await axios.put(`/organizations/${id}`, organization);
        toast({ title: t("organization.organization_updated"), status: "success" });
      } else {
        await axios.post('/organizations', organization);
        toast({ title: t("organization.organization_created"), status: "success" });
      }
      navigate(isOrganizationSelfSettings
        ? location.pathname
        : "/app/organizations");
    } catch (error) {
      console.error('Error saving organization:', error);
      toast({ title: t("organization.organization_save_error"), status: "error" });
    }
  };

  const cardBg = useColorModeValue("white", "gray.800");
  const sectionBg = useColorModeValue("gray.50", "gray.700");

  return (
    <Box mx="auto" p={6} maxW="container.md">
      <Heading as="h2" size="lg" mb={6} textAlign="center">
        {isOrganizationSelfSettings ? t("organization.settings") : id ? t("organization.edit_organization") : t("organization.create_organization")}
      </Heading>

      <Card bg={cardBg} shadow="md" borderRadius="lg">
        <CardHeader pb={0}>
          <Text fontSize="lg" fontWeight="bold">
            {isOrganizationSelfSettings ? t("organization.update_details") : t("organization.manage_organization")}
          </Text>
        </CardHeader>
        <CardBody>
          <form onSubmit={handleSubmit}>
            <VStack spacing={6} align="stretch">
              <OrganizationGeneralFields
                values={organization}
                onChange={handleChange}
                sectionBg={sectionBg}
                showNotes={!isOrganizationSelfSettings}
                labels={{
                  generalInfo: t("organization.general_info"),
                  name: t("organization.organization_name"),
                  contact: t("organization.contact_person"),
                  title: t("organization.title"),
                  contactInfo: t("organization.contact_info"),
                  email: t("organization.email"),
                  phone: t("organization.phone"),
                  location: t("organization.location"),
                  address: t("organization.address"),
                  city: t("organization.city"),
                  state: t("organization.state"),
                  country: t("organization.country"),
                  zip: t("organization.zip"),
                  notes: t("organization.notes"),
                  notesPlaceholder: t("organization.notes_placeholder"),
                }}
              />

              <Divider />

              <OrganizationBillingFields
                values={organization}
                onChange={handleChange}
                onBillingSameAsOrgChange={(value) =>
                  setOrganization((prev) => ({ ...prev, billing_same_as_org: value }))
                }
                sectionBg={sectionBg}
                showBillingNotes={!isOrganizationSelfSettings}
                labels={{
                  billingInfo: t("organization.billing_info"),
                  billingSameAsOrg: t("organization.billing_same_as_org"),
                  yes: t("common.yes"),
                  no: t("common.no"),
                  billingAddress: t("organization.billing_address"),
                  billingCity: t("organization.billing_city"),
                  billingState: t("organization.billing_state"),
                  billingZip: t("organization.billing_zip"),
                  billingCountry: t("organization.billing_country"),
                  billingCompanyName: t("organization.billing_company_name"),
                  vatOrTaxNumber: t("organization.vat_or_tax_number"),
                  organisationsnummer: t("organization.organisationsnummer"),
                  currency: t("organization.currency"),
                  billingMethod: t("organization.billing_method"),
                  select: t("common.select"),
                  billingMethodInvoice: t("organization.billing_method_invoice"),
                  billingMethodDebit: t("organization.billing_method_debit"),
                  billingMethodCard: t("organization.billing_method_card"),
                  billingMethodPeppol: t("organization.billing_method_edi_peppol"),
                  other: t("common.other"),
                  billingEmail: t("organization.billing_email"),
                  peppol: t("organization.peppol"),
                  gln: t("organization.gln"),
                  billingMethodOther: t("organization.billing_method_other"),
                  referencePerson: t("organization.reference_person"),
                  referenceNumber: t("organization.reference_number"),
                  costCenter: t("organization.cost_center"),
                  billingNotes: t("organization.billing_notes"),
                  billingNotesPlaceholder: t("organization.billing_notes_placeholder"),
                }}
              />

              <TourModeActionButton type="submit" colorScheme="green" variant="outline" isTour={isTour}>
                {id ? t("organization.update") : t("organization.create")}
              </TourModeActionButton>
            </VStack>
          </form>
        </CardBody>
      </Card>
    </Box>
  );
};

export default OrganizationForm;
